import AccountTreeIcon from "@mui/icons-material/AccountTree";
import ExpandIcon from "@mui/icons-material/ExpandMore";
import HandshakeIcon from "@mui/icons-material/Handshake";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import SwapHorizIcon from "@mui/icons-material/SwapHoriz";
import {
  Alert,
  Box,
  Button,
  ButtonBase,
  Collapse,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  Tooltip,
  Typography,
} from "@mui/material";
import { ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { Stage } from "../../shared/CaseConfiguration";
import { useCurrency } from "../../context/CurrencyContext";
import { useFeatureFlags } from "../../context/FeatureFlagsContext";
import { useTheme } from "../../context/ThemeContext";
import { OutcomeLedger } from "../../shared/Analytics";
import { percentage } from "../../shared/percentage";
import {
  signedBar,
  signedTone,
  TABULAR_NUMERALS,
  toneColors,
} from "../general/dataAtoms/shared";
import { DialogShell } from "../general/DialogShell";
import { SectionLabel } from "../general/SectionLabel";
import { COLUMN_HEADING } from "../general/tableStyles";
import { TonalBadge } from "../general/TonalBadge";
import {
  LedgerDagDialog,
  LedgerDagRef,
  LedgerGraphView,
} from "./LedgerDagDialog";
import { buildBreakdown, RECOVERY_NOT_APPLICABLE } from "./OutcomeBreakdown";

export type IssueOutcome = {
  issueId: string;
  title: string;
  opponentStatuses: (
    | {
        // names of opponents
        names: string[];
        // if opponents are settled, there is a settlement balance
        status: "settled";
        // balance of all settlement payments (received and paid)
        settlementAmountBalance: number;
      }
    | {
        // names of opponents
        names: string[];
        // not settled, so either won or lost
        status: "won" | "lost";
      }
  )[];
};

export interface JudgementDetail {
  probability: number;
  judgementValue: number;
  netResult: number;
  issueOutcomes: IssueOutcome[];
  // settlement status message: fully settled, not settled, or partially settled
  settlementStatus: string[];
  // the stage it settled in, where it is an assumed settlement and no judgment
  settledAt?: Stage;
  enforcement?: { worstAmount: number; costs: number };
  hasAgreements?: {
    lawFirm: boolean;
    barrister: boolean;
  };
  // server-emitted ledger: every numeric contribution to each entity's netResult
  ledger: OutcomeLedger;
  /** Dev-only: identifies the outcome for the on-demand ledger DAG view. */
  dag?: LedgerDagRef;
}

/** Room for the breakdown with its two columns of amounts, in px. */
const WIDTH = 800;

/** The size of the ring that shows the probability, and of its hole, in px. */
const RING_SIZE = 54;
const RING_HOLE_SIZE = 38;

/**
 * One of several outcomes with the same net result: its title opens and closes
 * what the outcome consists of.
 */
const OutcomePanel = ({
  title,
  initiallyOpen,
  children,
}: {
  title: string;
  initiallyOpen: boolean;
  children: ReactNode;
}) => {
  const { theme } = useTheme();
  const [isOpen, setIsOpen] = useState(initiallyOpen);

  return (
    <Box
      sx={{
        border: 1,
        borderColor: "divider",
        borderRadius: 2.5,
        overflow: "hidden",
      }}
    >
      <ButtonBase
        onClick={() => setIsOpen(!isOpen)}
        aria-expanded={isOpen}
        sx={{
          width: "100%",
          justifyContent: "flex-start",
          gap: 1.25,
          px: 1.75,
          py: 1.375,
          textAlign: "left",
          typography: "body2",
          fontWeight: 600,
          bgcolor: isOpen ? "background.default" : "transparent",
        }}
      >
        <ExpandIcon
          fontSize="small"
          sx={{
            flex: "none",
            color: "text.secondary",
            // points to the right while the outcome is closed
            transform: isOpen ? "none" : "rotate(-90deg)",
            transition: theme.transitions.create("transform"),
          }}
        />
        {title}
      </ButtonBase>
      <Collapse in={isOpen}>
        <Box
          sx={{ px: 2, pt: 1.75, pb: 2, borderTop: 1, borderColor: "divider" }}
        >
          {children}
        </Box>
      </Collapse>
    </Box>
  );
};

export const OutcomeSummaryDialog = ({
  open,
  onClose,
  judgments,
  probability,
  partyName,
  isClient = false,
}: {
  open: boolean;
  onClose: () => void;
  // the outcomes with the same net result, the likeliest first
  judgments: JudgementDetail[];
  // of any of the outcomes
  probability: number;
  // whose outcomes these are
  partyName?: string;
  // the party is a client: its firm is yours, valued by your fee structure,
  // where any other firm's result is never calculated
  isClient?: boolean;
}) => {
  const { t } = useTranslation();
  const { currency } = useCurrency();
  const { theme } = useTheme();
  const { palette } = theme;
  const tones = toneColors(theme);
  const featureFlags = useFeatureFlags();
  // dev-only ledger graph views (see LedgerDagDialog)
  const [graph, setGraph] = useState<{
    dag: LedgerDagRef;
    view: LedgerGraphView;
  }>();
  const { netResult } = judgments[0];

  // a figure is coloured by its sign; nothing is neither a gain nor a loss
  const inkOf = (amount: number) =>
    amount === 0 ? "text.primary" : tones[signedTone(amount)];
  const statusTones = {
    won: palette.data.positive,
    lost: palette.data.negative,
    settled: palette.warning,
  };

  const header = (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
        flexWrap: "wrap",
        gap: 2.5,
        // the inset of the content under it
        px: 2.5,
        py: 2.5,
        borderBottom: 1,
        borderColor: "divider",
      }}
    >
      <div>
        <Typography
          variant="caption"
          sx={{ display: "block", fontWeight: 600, color: "text.secondary" }}
        >
          {t("outcomeDialog.title")}
        </Typography>
        <Typography
          variant="h4"
          component="p"
          sx={{
            fontWeight: 700,
            lineHeight: 1.15,
            letterSpacing: "-.02em",
            color: inkOf(netResult),
            ...TABULAR_NUMERALS,
          }}
        >
          {currency.format(netResult)}
        </Typography>
        <Typography
          variant="caption"
          sx={{ display: "block", mt: 0.25, color: "text.secondary" }}
        >
          {[partyName, t("outcomeDialog.group", { count: judgments.length })]
            .filter((part) => part !== undefined)
            .join(" · ")}
        </Typography>
      </div>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
        <Box sx={{ textAlign: "right" }}>
          <Typography
            variant="caption"
            sx={{ display: "block", fontWeight: 600, color: "text.secondary" }}
          >
            {t("outcomeDialog.probability")}
          </Typography>
          <Typography
            variant="h5"
            component="p"
            sx={{ fontWeight: 700, ...TABULAR_NUMERALS }}
          >
            {percentage.format(probability)}
          </Typography>
        </Box>
        {/* as much of the ring is filled as the outcomes are likely */}
        <Box
          sx={{
            flex: "none",
            width: RING_SIZE,
            height: RING_SIZE,
            borderRadius: "50%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            background: `conic-gradient(${
              signedBar(theme, netResult).bgcolor
            } 0 ${probability * 100}%, ${palette.action.hover} ${
              probability * 100
            }% 100%)`,
          }}
        >
          <Box
            sx={{
              width: RING_HOLE_SIZE,
              height: RING_HOLE_SIZE,
              borderRadius: "50%",
              bgcolor: "background.paper",
            }}
          />
        </Box>
      </Box>
    </Box>
  );

  return (
    <>
      <DialogShell
        open={open}
        onClose={onClose}
        title={t("outcomeDialog.title")}
        header={header}
        width={WIDTH}
        // the dialog is closed by the button at its foot
        closeButton={false}
        actions={
          <Button onClick={onClose} color="primary" variant="contained">
            {t("outcomeDialog.close")}
          </Button>
        }
      >
        {judgments.map((judgement: JudgementDetail, index) => {
          // a settlement is no judgment to enforce
          const nonEnforceable =
            judgement.settledAt !== undefined
              ? 0
              : (judgement.enforcement?.worstAmount ?? 0) -
                judgement.judgementValue;
          const hasNoIssues = judgement.issueOutcomes.length === 0;

          // Bill rows read directly from server-aggregated totals. Σ rows === netResult.
          const financialBreakdown = buildBreakdown(
            judgement.ledger.totals.party,
            t,
          );

          const body = (
            <Stack spacing={2}>
              {nonEnforceable !== 0 && (
                <Alert severity={nonEnforceable < 0 ? "warning" : "error"}>
                  {t(
                    nonEnforceable < 0
                      ? "case.analytics.outcomes.warning.enforcementValue"
                      : "case.analytics.outcomes.warning.debt",
                    { value: currency.format(-nonEnforceable) },
                  )}
                </Alert>
              )}

              <div>
                <SectionLabel component="h3">
                  {t("outcomeDialog.issueOutcomes")}
                </SectionLabel>
                {hasNoIssues ? (
                  <Typography
                    variant="body2"
                    sx={{ mt: 1, color: "text.secondary" }}
                  >
                    {t("outcomeDialog.noIssues")}
                  </Typography>
                ) : (
                  <Box sx={{ mt: 0.5 }}>
                    {judgement.issueOutcomes.flatMap(
                      ({ issueId, title, opponentStatuses }) =>
                        opponentStatuses.map((opponents) => {
                          const badge = (
                            <TonalBadge tone={statusTones[opponents.status]}>
                              {t(`outcomeDialog.status_${opponents.status}`)}
                            </TonalBadge>
                          );
                          return (
                            <Box
                              key={`${issueId}-${opponents.status}-${opponents.names.join(",")}`}
                              sx={{
                                display: "grid",
                                gridTemplateColumns:
                                  "minmax(0, 1.5fr) minmax(0, 1.2fr) auto",
                                columnGap: 1.5,
                                alignItems: "center",
                                py: 0.875,
                                borderBottom: 1,
                                borderColor: "divider",
                              }}
                            >
                              <Typography
                                variant="body2"
                                sx={{
                                  fontWeight: 600,
                                  overflowWrap: "anywhere",
                                }}
                              >
                                {title}
                              </Typography>
                              <Typography
                                variant="body2"
                                sx={{
                                  color: "text.secondary",
                                  overflowWrap: "anywhere",
                                }}
                              >
                                {opponents.names.join(", ")}
                              </Typography>
                              {opponents.status === "settled" ? (
                                <Tooltip
                                  arrow
                                  disableInteractive
                                  title={t("outcomeDialog.settledTooltip", {
                                    amount: currency.format(
                                      opponents.settlementAmountBalance ?? 0,
                                    ),
                                  })}
                                >
                                  <span>{badge}</span>
                                </Tooltip>
                              ) : (
                                badge
                              )}
                            </Box>
                          );
                        }),
                    )}
                  </Box>
                )}
              </div>

              {judgement.settlementStatus &&
                judgement.settlementStatus.length > 0 && (
                  <Stack
                    direction="row"
                    spacing={1}
                    useFlexGap
                    sx={{ flexWrap: "wrap" }}
                  >
                    {judgement.settlementStatus.map((status, index) => (
                      <Box
                        key={index}
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 0.875,
                          px: 1.375,
                          py: 0.625,
                          borderRadius: 1.5,
                          bgcolor: "action.hover",
                          color: "text.secondary",
                          typography: "caption",
                          fontWeight: 600,
                        }}
                      >
                        <HandshakeIcon sx={{ flex: "none", fontSize: 15 }} />
                        {status}
                      </Box>
                    ))}
                  </Stack>
                )}

              {/* bill-style financial summary */}
              <Table
                size="small"
                sx={{
                  "& .MuiTableCell-root": { px: 0, py: 0.625 },
                  "& .MuiTableCell-root:not(:first-of-type)": { pl: 1.5 },
                  // the rows of the bill have no lines between them
                  "& .MuiTableBody-root .MuiTableCell-root": {
                    borderBottom: 0,
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>
                      <SectionLabel component="h3">
                        {t("outcomeDialog.financialBreakdown")}
                      </SectionLabel>
                    </TableCell>
                    <TableCell align="right" sx={COLUMN_HEADING}>
                      {t("outcomeDialog.bill.recoveredColumn")}
                    </TableCell>
                    <TableCell align="right" sx={COLUMN_HEADING}>
                      {t("outcomeDialog.bill.amountColumn")}
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {financialBreakdown.map(
                    (
                      {
                        label,
                        amount,
                        recovered,
                        subtotal,
                        detail,
                        icon,
                        counterparties,
                      },
                      index,
                    ) => {
                      // a subtotal and a detail restate what other rows hold:
                      // they stand back in quiet ink and italics
                      const isQuiet = subtotal || detail;
                      const quiet = isQuiet
                        ? { color: "text.secondary", fontStyle: "italic" }
                        : undefined;
                      // Width occupied by a body2-sized leading icon plus
                      // its trailing gap (Stack spacing={0.5}). Used to
                      // align indented detail rows beneath the parent row's
                      // label text — past the icon.
                      const iconWidth = theme.spacing(2);
                      const tooltipTitle = counterparties &&
                        counterparties.length > 0 && (
                          <Stack spacing={0.25}>
                            {counterparties.map((name) => (
                              <Typography key={name} variant="caption">
                                {name}
                              </Typography>
                            ))}
                          </Stack>
                        );
                      const rule = subtotal
                        ? { borderTop: 1, borderColor: "divider" }
                        : undefined;
                      const recoveredCell = (() => {
                        if (recovered === undefined) return null;
                        if (recovered === RECOVERY_NOT_APPLICABLE)
                          return (
                            <Typography
                              variant="body2"
                              sx={{ color: "text.disabled" }}
                            >
                              –
                            </Typography>
                          );
                        return (
                          <Typography
                            variant="body2"
                            sx={{
                              // green only where something came back, and
                              // not on the rows that stand back
                              color:
                                !isQuiet && recovered > 0
                                  ? tones.favourable
                                  : "text.secondary",
                              fontStyle: isQuiet ? "italic" : undefined,
                              ...TABULAR_NUMERALS,
                            }}
                          >
                            {recovered > 0
                              ? `+${currency.format(recovered)}`
                              : currency.format(0)}
                          </Typography>
                        );
                      })();
                      return (
                        <TableRow key={`breakdown-${index}`}>
                          <TableCell sx={rule}>
                            <Stack
                              direction="row"
                              spacing={0.5}
                              sx={{
                                alignItems: "center",
                                pl: detail ? iconWidth : undefined,
                              }}
                            >
                              {icon === "settlement" && (
                                <HandshakeIcon
                                  sx={{
                                    fontSize: theme.typography.body2.fontSize,
                                    color: "text.secondary",
                                  }}
                                />
                              )}
                              <Typography variant="body2" sx={quiet}>
                                {label}
                              </Typography>
                              {tooltipTitle && (
                                <Tooltip
                                  arrow
                                  disableInteractive
                                  title={tooltipTitle}
                                >
                                  <InfoOutlinedIcon
                                    sx={{
                                      fontSize: theme.typography.body2.fontSize,
                                      color: "text.secondary",
                                    }}
                                  />
                                </Tooltip>
                              )}
                            </Stack>
                          </TableCell>
                          <TableCell align="right" sx={rule}>
                            {recoveredCell}
                          </TableCell>
                          <TableCell align="right" sx={rule}>
                            <Typography
                              variant="body2"
                              sx={{
                                fontWeight: isQuiet ? undefined : 600,
                                color: tones[signedTone(amount)],
                                ...quiet,
                                ...TABULAR_NUMERALS,
                              }}
                            >
                              {`${!isQuiet && amount >= 0 ? "+" : ""}${currency.format(amount)}`}
                            </Typography>
                          </TableCell>
                        </TableRow>
                      );
                    },
                  )}

                  {/* what the rows above come to for the party */}
                  <TableRow
                    sx={{
                      // doubled, to hold against the padding the table sets
                      "&& .MuiTableCell-root": {
                        pt: 1.25,
                        borderTop: financialBreakdown.length > 0 ? 1 : 0,
                        borderColor: "divider",
                      },
                    }}
                  >
                    <TableCell colSpan={2}>
                      <Typography sx={{ fontWeight: 700 }}>
                        {t("outcomeDialog.partyNetResult")}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      <Typography
                        sx={{
                          fontWeight: 700,
                          color: inkOf(judgement.netResult),
                          ...TABULAR_NUMERALS,
                        }}
                      >
                        {currency.format(judgement.netResult)}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </TableBody>
              </Table>

              {/* Law firm / barrister secondary info */}
              <Stack
                spacing={0.5}
                sx={{ pt: 1.25, borderTop: 1, borderColor: "divider" }}
              >
                {[
                  // the firm's result needs your fee structure: a client's
                  // says where it is missing, any other firm's has none
                  ...(judgement.ledger.totals.netResult.lawFirm !== "unknown"
                    ? [
                        {
                          label: t("outcomeDialog.lawFirmNetResult"),
                          amount: judgement.ledger.totals.netResult.lawFirm,
                        },
                      ]
                    : isClient
                      ? [
                          {
                            label: t("outcomeDialog.lawFirmNetResult"),
                            amount: undefined,
                          },
                        ]
                      : []),
                  ...(judgement.hasAgreements?.barrister
                    ? [
                        {
                          label: t("outcomeDialog.barristerNetResult"),
                          amount: judgement.ledger.totals.netResult.barrister,
                        },
                      ]
                    : []),
                ].map(({ label, amount }) => (
                  <Stack
                    key={label}
                    direction="row"
                    sx={{ justifyContent: "space-between" }}
                  >
                    <Typography
                      variant="body2"
                      sx={{ color: "text.secondary" }}
                    >
                      {label}
                    </Typography>
                    {amount === undefined ? (
                      <Typography
                        variant="body2"
                        sx={{ color: "text.secondary" }}
                      >
                        {t("outcomeDialog.lawFirmNetResultUnknown")}
                      </Typography>
                    ) : (
                      <Typography
                        variant="body2"
                        sx={{ fontWeight: 600, ...TABULAR_NUMERALS }}
                      >
                        {currency.format(amount)}
                      </Typography>
                    )}
                  </Stack>
                ))}
              </Stack>

              {/* dev-only: graph views of the ledger behind this outcome */}
              {featureFlags?.decisionTree && judgement.dag && (
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "flex-end",
                    gap: 1,
                  }}
                >
                  <Button
                    size="small"
                    startIcon={<SwapHorizIcon />}
                    onClick={() =>
                      setGraph({ dag: judgement.dag!, view: "flow" })
                    }
                  >
                    Money flow
                  </Button>
                  <Button
                    size="small"
                    startIcon={<AccountTreeIcon />}
                    onClick={() =>
                      setGraph({ dag: judgement.dag!, view: "items" })
                    }
                  >
                    Ledger graph
                  </Button>
                </Box>
              )}
            </Stack>
          );
          // a single outcome needs no title to tell it from the others
          return judgments.length > 1 ? (
            <OutcomePanel
              key={index}
              title={
                judgement.settledAt !== undefined
                  ? t("outcomeDialog.settledTitle", {
                      probability: percentage.format(judgement.probability),
                      stage: t(`case.stages.${Stage[judgement.settledAt]}`),
                      netResult: currency.format(judgement.netResult),
                    })
                  : t("outcomeDialog.judgementTitle", {
                      probability: percentage.format(judgement.probability),
                      judgementValue: currency.format(judgement.netResult),
                    })
              }
              // the likeliest outcome is the one to read first
              initiallyOpen={index === 0}
            >
              {body}
            </OutcomePanel>
          ) : (
            <Box key={index}>{body}</Box>
          );
        })}
      </DialogShell>
      {graph && (
        <LedgerDagDialog
          dag={graph.dag}
          view={graph.view}
          open
          onClose={() => setGraph(undefined)}
        />
      )}
    </>
  );
};
